import React, { useState, useEffect } from 'react';
import { Target, Calendar } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

interface WeeklyStats {
  total_accounts: number;
  weekly_quota: number;
  week_start: string;
  week_end: string;
}

const WeeklyQuota: React.FC = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState<WeeklyStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [editingQuota, setEditingQuota] = useState(false);
  const [newQuota, setNewQuota] = useState('');
  const isAdmin = user?.role === 'admin' || user?.role === 'supervisor';

  useEffect(() => {
    if (!user) return;
    fetchWeeklyStats();
  }, [user]);

  const fetchWeeklyStats = async () => {
    try {
      setLoading(true);

      // Get weekly stats including quota
      const { data, error } = await supabase
        .rpc('get_weekly_accounts_count', {
          user_id: user?.id
        });

      if (error) throw error;

      if (!data?.[0]) {
        throw new Error('No stats returned');
      }

      // Get user's RP-based quota reduction
      const { data: rewardsData } = await supabase
        .from('user_rewards')
        .select('quota_reduction')
        .eq('user_id', user?.id)
        .single();

      // Apply RP-based reduction to the base quota
      const baseQuota = data[0].weekly_quota;
      const reduction = rewardsData?.quota_reduction || 0;
      const adjustedQuota = Math.max(0, baseQuota - reduction);

      setStats({
        ...data[0],
        weekly_quota: adjustedQuota
      });
    } catch (error) {
      console.error('Error fetching weekly stats:', error);
      toast.error('Failed to fetch weekly statistics');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateQuota = async () => {
    if (!isAdmin) return;

    const quota = parseInt(newQuota);
    if (isNaN(quota) || quota < 0) {
      toast.error('Please enter a valid number');
      return;
    }

    try {
      const { error } = await supabase
        .from('account_quotas')
        .insert([{ weekly_quota: quota }]);

      if (error) throw error;

      toast.success('Weekly quota updated successfully');
      setEditingQuota(false);
      fetchWeeklyStats();
    } catch (error) {
      console.error('Error updating quota:', error);
      toast.error('Failed to update quota');
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse bg-white dark:bg-gray-800 p-4 rounded-lg shadow flex items-center space-x-4">
        <div className="h-8 w-8 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
        <div className="flex-1">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
          <div className="mt-2 h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  if (!stats) return null;

  const progressPercentage = stats.weekly_quota > 0 
    ? (stats.total_accounts / stats.weekly_quota) * 100 
    : stats.total_accounts > 0 ? 100 : 0;

  const weekStart = new Date(stats.week_start).toLocaleDateString();
  const weekEnd = new Date(stats.week_end).toLocaleDateString();

  return (
    <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <Target className="h-6 w-6 text-indigo-600 dark:text-indigo-400 mr-2" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Weekly Progress</h3>
        </div>
        <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
          <Calendar className="h-4 w-4 mr-1" />
          <span>{weekStart} - {weekEnd}</span>
        </div>
      </div>

      <div className="mb-4">
        <div className="flex justify-between mb-1">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {stats.total_accounts} {stats.weekly_quota > 0 ? `of ${stats.weekly_quota} accounts` : '(No Quota)'}
          </span>
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {Math.round(progressPercentage)}%
          </span>
        </div>
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
          <div
            className={`h-2.5 rounded-full ${
              progressPercentage >= 100
                ? 'bg-green-600 dark:bg-green-500'
                : 'bg-indigo-600 dark:bg-indigo-500'
            }`}
            style={{ width: `${Math.min(100, progressPercentage)}%` }}
          ></div>
        </div>
      </div>

      {isAdmin && (
        <div className="mt-4">
          {editingQuota ? (
            <div className="flex space-x-2">
              <input
                type="number"
                min="0"
                value={newQuota}
                onChange={(e) => setNewQuota(e.target.value)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                placeholder="Enter weekly quota"
              />
              <button
                onClick={handleUpdateQuota}
                className="px-3 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600"
              >
                Save
              </button>
              <button
                onClick={() => setEditingQuota(false)}
                className="px-3 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={() => {
                setNewQuota(stats.weekly_quota.toString());
                setEditingQuota(true);
              }}
              className="text-sm text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300"
            >
              Update Weekly Quota
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default WeeklyQuota;